import { gql, useQuery } from '@apollo/client';
import { AppHelmet, fragmentAppHelmet } from '@pendwa/common-browser/components/AppHelmet/index.js';
import { ErrorBanner } from '@pendwa/common-browser/components/ErrorBanner/index.js';
import { LayoutContainer } from '@pendwa/common-browser/components/LayoutContainer/index.js';
import { LayoutPageSection } from '@pendwa/common-browser/components/LayoutPageSection/index.js';
import { LayoutSpacer } from '@pendwa/common-browser/components/LayoutSpacer/index.js';
import { LoadingPage } from '@pendwa/common-browser/components/Loading/LoadingPage/index.js';
import { Footer } from '@pendwa/common-browser/components/Navs/Footer/index.js';
import { Nav } from '@pendwa/common-browser/components/Navs/Nav/index.js';
import { extractGraphQLErrorMessage } from '@pendwa/common-browser/core/apollo/utils.js';
import { reloadPage } from '@pendwa/common-browser/core/appLocation/index.js';
import { app_store_credentials_is_logged_in } from '@pendwa/common-browser/core/appStore/credentials.js';
import { rootRoutes } from '@pendwa/common/core/rootRoutes/index.js';
import { isDefinedAndNotNull } from '@pendwa/common/utils/isDefinedAndNotNull.js';
import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { getFooterConfig } from '../../../../core/footer/config/index.js';
import { getNavConfig } from '../../../../core/nav/config/index.js';
import { PageEntryPersonQuery, PageEntryPersonQueryVariables } from '../../../../generated/graphql.js';
import { HeroPerson } from './components/HeroPerson/index.js';
import { PersonGroupRender } from './components/PersonGroupRender/index.js';

const GRAPHQL_QUERY = gql`
  query PageEntryPerson($user_name: String!) {
    pageEntry {
      data {
        pageEntryPerson(body: { user_name: $user_name }) {
          uuid
          person {
            ...fragmentPageEntryPerson
          }
          show {
            uuid
            movie {
              uuid
              title
              path
              cover_on_view {
                uuid
                path
              }
              fallback_cover_path
            }
            tv {
              uuid
              title
              path
              cover_on_view {
                uuid
                path
              }
              fallback_cover_path
            }
          }
          crossword {
            uuid
            title
            number
            path
            image_on_view {
              uuid
              path
            }
            fallback_image_path
          }
          helmet {
            ...fragmentAppHelmet
          }
        }
      }
    }
  }

  fragment fragmentPageEntryPerson on TypePerson {
    uuid
    name
    summary
    biography
    path
    image_on_view {
      uuid
      path
    }
    fallback_image_path
  }

  ${fragmentAppHelmet}
`;

const Person: React.FC<{
  user_name: string;
}> = (props) => {
  const history = useHistory();

  const queryResult = useQuery<PageEntryPersonQuery, PageEntryPersonQueryVariables>(GRAPHQL_QUERY, {
    fetchPolicy: app_store_credentials_is_logged_in ? 'cache-and-network' : 'cache-first',
    variables: {
      user_name: props.user_name,
    },
  });

  if (queryResult.loading && !queryResult.data) {
    return <LoadingPage />;
  }

  if (queryResult.error) {
    return (
      <ErrorBanner
        message={
          extractGraphQLErrorMessage(queryResult.error) ||
          'An error occurred while loading data on this page.'
        }
        actionButtons={[
          {
            text: 'Reload',
            onClick: () => reloadPage(),
          },
        ]}
      />
    );
  }

  const person = queryResult.data?.pageEntry.data?.pageEntryPerson?.person;
  const shows = queryResult.data?.pageEntry.data?.pageEntryPerson?.show;
  const crosswords = queryResult.data?.pageEntry.data?.pageEntryPerson?.crossword;
  const helmet = queryResult.data?.pageEntry.data?.pageEntryPerson?.helmet;

  if (!person || !shows || !crosswords) {
    return (
      <ErrorBanner
        message={extractGraphQLErrorMessage(queryResult.error) || '404: Not Found.'}
        actionButtons={[
          {
            text: 'Back to home',
            onClick: () => history.push(rootRoutes.home.main.get()),
          },
        ]}
      />
    );
  }

  if (!helmet) {
    return (
      <ErrorBanner
        message={
          extractGraphQLErrorMessage(queryResult.error) ||
          'An error occurred while loading data on this page.'
        }
        actionButtons={[
          {
            text: 'Reload',
            onClick: () => reloadPage(),
          },
        ]}
      />
    );
  }

  return (
    <>
      <AppHelmet {...helmet} />
      <LayoutSpacer>
        <HeroPerson item={person} />

        <PersonGroupRender
          group={{
            key: 'aae4e97e-9bf5-47f6-8f3f-7fbfc55230c6', // # random uuid,
            title: 'Movies & Shows',
            items: shows
              .map((item) => {
                if (item.movie) {
                  return {
                    key: item.movie.uuid,
                    title: item.movie.title,
                    path: item.movie.path,
                    image_path: item.movie.cover_on_view?.path ?? item.movie.fallback_cover_path,
                  };
                } else if (item.tv) {
                  return {
                    key: item.tv.uuid,
                    title: item.tv.title,
                    path: item.tv.path,
                    image_path: item.tv.cover_on_view?.path ?? item.tv.fallback_cover_path,
                  };
                } else {
                  return undefined;
                }
              })
              .filter(isDefinedAndNotNull),
          }}
        />

        <PersonGroupRender
          group={{
            key: 'bbf4e97e-9bf5-47f5-8f3f-7fbfc55230c7', // # random uuid,
            title: 'Crosswords',
            items: crosswords
              .map((item) => {
                return {
                  key: item.uuid,
                  title: `${item.number}`,
                  path: item.path,
                  image_path: item.image_on_view?.path ?? item.fallback_image_path,
                };
              })
              .filter(isDefinedAndNotNull),
          }}
        />
      </LayoutSpacer>
    </>
  );
};

const Layout: React.FC<{
  user_name: string;
}> = (props) => {
  return (
    <>
      <Nav getConfig={getNavConfig} />

      <LayoutPageSection>
        <LayoutContainer>
          <Person user_name={props.user_name} />
        </LayoutContainer>
      </LayoutPageSection>

      <LayoutPageSection>
        <LayoutContainer>
          <Footer getConfig={getFooterConfig} />
        </LayoutContainer>
      </LayoutPageSection>
    </>
  );
};

export function Screen() {
  const history = useHistory();
  const match = useRouteMatch<{
    user_name: string;
  }>();

  if (!match.params.user_name) {
    return (
      <LayoutPageSection>
        <LayoutContainer>
          <ErrorBanner
            message={'Invalid link'}
            actionButtons={[
              {
                text: `Go To ${browserRootConfig.project.name}`,
                onClick: () => history.push(rootRoutes.home.main.get()),
              },
            ]}
          />
        </LayoutContainer>
      </LayoutPageSection>
    );
  }

  const user_name = match.params.user_name;

  return (
    <>
      <Layout user_name={user_name} />
    </>
  );
}
